import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { TimeTrackingRepository } from '../domain/time-tracking.repository';
import {
  ManualTimeEntryInput,
  StartTimerInput,
  TimeDailySlice,
  TimeEntry,
  TimeTag,
  UpdateTimeEntryInput
} from '../models/time-tracking.model';

interface TimeEntryTagRow {
  tag_id: number;
}

interface TimeEntryRow {
  id: number;
  activity_id: number | null;
  task_id: number | null;
  started_at: string;
  ended_at: string | null;
  timezone: string;
  note: string | null;
  created_at: string;
  updated_at: string;
  time_entry_tags?: TimeEntryTagRow[] | null;
}

interface TimeTagRow {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface TimeDailySliceRow {
  entry_id: number;
  activity_id: number | null;
  task_id: number | null;
  local_date: string;
  duration_seconds: number;
}

type NormalizedStartInput = Required<Pick<StartTimerInput, 'activityId' | 'taskId' | 'startedAt' | 'timezone'>> & {
  note: string | null;
};

type NormalizedManualInput = Required<Pick<ManualTimeEntryInput, 'activityId' | 'taskId' | 'startedAt' | 'endedAt' | 'timezone'>> & {
  note: string | null;
};

const ENTRY_SELECT = 'id,activity_id,task_id,started_at,ended_at,timezone,note,created_at,updated_at,time_entry_tags(tag_id)';

@Injectable()
export class SupabaseTimeTrackingRepository extends TimeTrackingRepository {
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async getEntries(): Promise<TimeEntry[]> {
    const { data, error } = await this.client
      .from('time_entries')
      .select(ENTRY_SELECT)
      .order('started_at', { ascending: false })
      .order('id', { ascending: false });
    if (error) throw error;
    return this.enrichTaskTitles(
      (data ?? []).map(row => this.mapEntry(row as unknown as TimeEntryRow))
    );
  }

  async getActiveEntries(): Promise<TimeEntry[]> {
    const { data, error } = await this.client
      .from('time_entries')
      .select(ENTRY_SELECT)
      .is('ended_at', null)
      .order('started_at')
      .order('id');
    if (error) throw error;
    return this.enrichTaskTitles(
      (data ?? []).map(row => this.mapEntry(row as unknown as TimeEntryRow))
    );
  }

  async getActivityTagIds(activityId: number): Promise<number[]> {
    const ids = new Set<number>();
    // Query only the requested activity, not the global newest 1,000 time entries.
    // Paginate so older activity-specific tags remain available.
    for (let offset = 0; offset < 20_000; offset += 1_000) {
      const { data, error } = await this.client.from('time_entries')
        .select('id,time_entry_tags(tag_id)')
        .eq('activity_id', activityId)
        .order('id', { ascending: true })
        .range(offset, offset + 999);
      if (error) throw error;
      for (const entry of data ?? []) {
        for (const tag of entry.time_entry_tags ?? []) ids.add(Number(tag.tag_id));
      }
      if ((data ?? []).length < 1_000) break;
    }
    return [...ids];
  }

  async getTags(): Promise<TimeTag[]> {
    const { data, error } = await this.client
      .from('time_tags')
      .select('id,name,created_at,updated_at')
      .order('name');
    if (error) throw error;
    return (data ?? []).map(row => this.mapTag(row as TimeTagRow));
  }

  async resolveTag(name: string): Promise<TimeTag> {
    const { data, error } = await this.client.rpc('resolve_time_tag', {
      p_name: name
    });
    if (error) throw error;
    return this.loadTag(Number(data));
  }

  async setEntryTags(entryId: number, tagIds: number[]): Promise<void> {
    const { error } = await this.client.rpc('set_time_entry_tags', {
      p_entry_id: entryId,
      p_tag_ids: tagIds
    });
    if (error) throw error;
  }

  async startTimer(input: NormalizedStartInput): Promise<TimeEntry> {
    const { data, error } = await this.client.rpc('start_time_entry', {
      p_activity_id: input.activityId,
      p_task_id: input.taskId,
      p_timezone: input.timezone,
      p_started_at: input.startedAt,
      p_note: input.note
    });
    if (error) throw error;
    return this.loadEntry(Number(data));
  }

  async stopTimer(entryId: number, endedAt: string): Promise<TimeEntry> {
    const { error } = await this.client.rpc('stop_time_entry', {
      p_entry_id: entryId,
      p_ended_at: endedAt
    });
    if (error) throw error;
    return this.loadEntry(entryId);
  }

  async cancelTimer(entryId: number): Promise<void> {
    const { error } = await this.client.rpc('cancel_time_entry', {
      p_entry_id: entryId
    });
    if (error) throw error;
  }

  async createManualEntry(input: NormalizedManualInput): Promise<TimeEntry> {
    const { data, error } = await this.client.rpc('create_manual_time_entry', {
      p_activity_id: input.activityId,
      p_task_id: input.taskId,
      p_started_at: input.startedAt,
      p_ended_at: input.endedAt,
      p_timezone: input.timezone,
      p_note: input.note
    });
    if (error) throw error;
    return this.loadEntry(Number(data));
  }

  async updateEntry(
    entryId: number,
    input: UpdateTimeEntryInput & {
      activityId: number | null;
      taskId: number | null;
      note: string | null;
    }
  ): Promise<TimeEntry> {
    const { error } = await this.client.rpc('update_time_entry', {
      p_entry_id: entryId,
      p_activity_id: input.activityId,
      p_task_id: input.taskId,
      p_started_at: input.startedAt,
      p_ended_at: input.endedAt,
      p_timezone: input.timezone,
      p_note: input.note
    });
    if (error) throw error;
    return this.loadEntry(entryId);
  }

  async updateEntryWithTags(
    entryId: number,
    input: UpdateTimeEntryInput & {
      activityId: number | null;
      taskId: number | null;
      note: string | null;
    },
    tagIds: number[]
  ): Promise<TimeEntry> {
    const { error } = await this.client.rpc('update_time_entry_with_tags', {
      p_entry_id: entryId,
      p_activity_id: input.activityId,
      p_task_id: input.taskId,
      p_started_at: input.startedAt,
      p_ended_at: input.endedAt,
      p_timezone: input.timezone,
      p_note: input.note,
      p_tag_ids: tagIds
    });
    if (error) throw error;
    return this.loadEntry(entryId);
  }

  async deleteEntry(entryId: number): Promise<void> {
    const { error } = await this.client
      .from('time_entries')
      .delete()
      .eq('id', entryId);
    if (error) throw error;
  }

  async getDailySlices(from: string, to: string): Promise<TimeDailySlice[]> {
    const { data, error } = await this.client.rpc('get_time_entry_daily_slices', {
      p_from: from,
      p_to: to
    });
    if (error) throw error;

    return ((data ?? []) as TimeDailySliceRow[]).map(row => ({
      entryId: row.entry_id,
      activityId: row.activity_id,
      taskId: row.task_id,
      localDate: row.local_date,
      durationSeconds: Number(row.duration_seconds)
    }));
  }

  private async loadEntry(entryId: number): Promise<TimeEntry> {
    const { data, error } = await this.client
      .from('time_entries')
      .select(ENTRY_SELECT)
      .eq('id', entryId)
      .single();
    if (error) throw error;
    const [entry] = await this.enrichTaskTitles([
      this.mapEntry(data as unknown as TimeEntryRow)
    ]);
    return entry;
  }

  private async enrichTaskTitles(entries: TimeEntry[]): Promise<TimeEntry[]> {
    const taskIds = [...new Set(
      entries.flatMap(entry => entry.taskId === null ? [] : [entry.taskId])
    )];
    if (!taskIds.length) return entries;

    const { data: occurrences, error: occurrenceError } = await this.client
      .from('task_occurrences')
      .select('id,task_definition_id')
      .in('id', taskIds);
    if (occurrenceError) throw occurrenceError;

    const definitionIds = [...new Set((occurrences ?? []).map(row => row.task_definition_id))];
    if (!definitionIds.length) return entries;

    const { data: definitions, error: definitionError } = await this.client
      .from('task_definitions')
      .select('id,title')
      .in('id', definitionIds);
    if (definitionError) throw definitionError;

    const definitionTitleById = new Map(
      (definitions ?? []).map(definition => [definition.id, definition.title])
    );
    const titleByOccurrenceId = new Map(
      (occurrences ?? []).map(occurrence => [
        occurrence.id,
        definitionTitleById.get(occurrence.task_definition_id) ?? null
      ])
    );

    return entries.map(entry => ({
      ...entry,
      taskTitle: entry.taskId === null ? null : titleByOccurrenceId.get(entry.taskId) ?? null
    }));
  }

  private async loadTag(tagId: number): Promise<TimeTag> {
    const { data, error } = await this.client
      .from('time_tags')
      .select('id,name,created_at,updated_at')
      .eq('id', tagId)
      .single();
    if (error) throw error;
    return this.mapTag(data as TimeTagRow);
  }

  private mapEntry(row: TimeEntryRow): TimeEntry {
    return {
      id: row.id,
      activityId: row.activity_id,
      taskId: row.task_id,
      taskTitle: null,
      startedAt: row.started_at,
      endedAt: row.ended_at,
      timezone: row.timezone,
      note: row.note,
      tagIds: (row.time_entry_tags ?? []).map(link => link.tag_id).sort((a, b) => a - b),
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  private mapTag(row: TimeTagRow): TimeTag {
    return {
      id: row.id,
      name: row.name,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }
}
